import { Controller } from "@hotwired/stimulus"
import { LogoHelper } from "~/helpers/logo_helper"
import { TempFormHelper } from "~/helpers/temp_form_helper"

export default class extends Controller {
  static get targets() {
    return [
      "domainInput",
      "filtersButton",
      "logo",
      "lastSearches",
      "resultsFrame",
      "resultsFrameLoader",
      "resultSummary",
      "resultDomain",
      "resultsCount",
      "resultsTitle",
      "clearFiltersButton",
      "loadMoreButton",
    ]
  }

  static get values() {
    return { domain: String, filters: Object, source: String }
  }

  connect() {
    this.selectedEmails = []
    this.loadResults()
    this.filtersHidden = true
  }

  loadResults() {
    this.domainValue = this.domainValue.replace(/^@/, "")

    if (!this.hasDomainValue || this.domainValue.trim() === "") {
      return
    }

    this.dispatch("loading")

    if (this.hasResultsFrameLoaderTarget) {
      this.resultsFrameLoaderTarget.classList.remove("d-none")
    }

    this.domainValue = App.hostnameFromURL(this.domainValue)

    // Disable the domain input temporarily to prevent triggering the autocomplete
    this.domainInputTarget.disabled = true
    this.domainInputTarget.value = this.domainValue
    this.domainInputTarget.disabled = false

    this.logoTarget.src = LogoHelper.companyLogoUrl(this.domainValue)
    this.logoTarget.classList.remove("d-none")

    if (this.hasLastSearchesTarget) {
      this.lastSearchesTarget.classList.add("d-none")
    }

    this.resultDomainTarget.classList.remove("d-none")
    this.resultsCountTarget.classList.remove("d-none")

    let limit = 10
    let offset = 0

    let url = App.api.domainSearch({
      domain: this.domainValue,
      limit,
      offset,
      type: this.#typeFilterValue(),
      department: this.filtersValue.department,
      requiredField: this.filtersValue.required_field,
      jobTitles: this.filtersValue.job_titles,
      verificationStatus: this.filtersValue.verification_status,
      savedLeads: this.#savedLeadsFilterValue(),
      format: "html",
      source: this.sourceValue,
    })

    let form = TempFormHelper.create(
      url,
      "post",
      (form) => {
        let i = 0
        let e = 0

        if (this.filtersValue["location_included_continents"]) {
          Array.from(this.filtersValue["location_included_continents"]).forEach((value) => {
            i += 1
            TempFormHelper.addHiddenInput(form, `location[include][${i}][continent]`, value)
          })
        }

        if (this.filtersValue["location_excluded_continents"]) {
          Array.from(this.filtersValue["location_excluded_continents"]).forEach((value) => {
            e += 1
            TempFormHelper.addHiddenInput(form, `location[exclude][${e}][continent]`, value)
          })
        }

        if (this.filtersValue["location_included_business_regions"]) {
          Array.from(this.filtersValue["location_included_business_regions"]).forEach((value) => {
            i += 1
            TempFormHelper.addHiddenInput(form, `location[include][${i}][business_region]`, value)
          })
        }

        if (this.filtersValue["location_excluded_business_regions"]) {
          Array.from(this.filtersValue["location_excluded_business_regions"]).forEach((value) => {
            e += 1
            TempFormHelper.addHiddenInput(form, `location[exclude][${e}][business_region]`, value)
          })
        }

        if (this.filtersValue["location_included_countries"]) {
          Array.from(this.filtersValue["location_included_countries"]).forEach((value) => {
            i += 1
            TempFormHelper.addHiddenInput(form, `location[include][${i}][country]`, value)
          })
        }

        if (this.filtersValue["location_excluded_countries"]) {
          Array.from(this.filtersValue["location_excluded_countries"]).forEach((value) => {
            e += 1
            TempFormHelper.addHiddenInput(form, `location[exclude][${e}][country]`, value)
          })
        }

        if (this.filtersValue["location_included_states"]) {
          Array.from(this.filtersValue["location_included_states"]).forEach((value) => {
            i += 1
            TempFormHelper.addHiddenInput(form, `location[include][${i}][country]`, "US")
            TempFormHelper.addHiddenInput(form, `location[include][${i}][state]`, value)
          })
        }

        if (this.filtersValue["location_excluded_states"]) {
          Array.from(this.filtersValue["location_excluded_states"]).forEach((value) => {
            e += 1
            TempFormHelper.addHiddenInput(form, `location[exclude][${e}][country]`, "US")
            TempFormHelper.addHiddenInput(form, `location[exclude][${e}][state]`, value)
          })
        }

        if (this.filtersValue["location_included_cities"]) {
          Array.from(this.filtersValue["location_included_cities"]).forEach((value) => {
            i += 1
            const { country, state, city } = this.#parseCityValue(value)
            if (state.length > 0) {
              TempFormHelper.addHiddenInput(form, `location[include][${i}][state]`, state)
            }
            TempFormHelper.addHiddenInput(form, `location[include][${i}][country]`, country)
            TempFormHelper.addHiddenInput(form, `location[include][${i}][city]`, city)
          })
        }

        if (this.filtersValue["location_excluded_cities"]) {
          Array.from(this.filtersValue["location_excluded_cities"]).forEach((value) => {
            e += 1
            const { country, state, city } = this.#parseCityValue(value)
            if (state.length > 0) {
              TempFormHelper.addHiddenInput(form, `location[exclude][${e}][state]`, state)
            }
            TempFormHelper.addHiddenInput(form, `location[exclude][${e}][country]`, country)
            TempFormHelper.addHiddenInput(form, `location[exclude][${e}][city]`, city)
          })
        }
      },
      this.resultsFrameTarget,
    )

    form.requestSubmit()
    this.toggleClearFilters()
    this.updateBrowserUrl()
  }

  // Perform the domain by form submit
  submit(event) {
    event.preventDefault()
    this.domainValue = this.domainInputTarget.value
    this.loadResults()
  }

  // Perform the domain search
  search(event) {
    event.preventDefault()

    this.domainValue = event.detail.domain === undefined ? event.params.domain : event.detail.domain

    if (this.domainValue === undefined) {
      window.location.href = "/search"
    } else {
      let anyFiltersSelected = Object.values(this.filtersValue).filter((value) => value).length > 0
      if (this.resultSummaryTarget.classList.contains("d-none") && !(this.filtersHidden || anyFiltersSelected)) {
        return
      }

      this.loadResults()
    }
  }

  // Update the number of results in the domain search form
  updateSummary({ detail: { count } }) {
    if (App.validateEmail(this.domainValue)) {
      this.resultDomainTarget.textContent = ""
    } else {
      this.resultDomainTarget.textContent = this.domainValue
    }

    this.resultsCountTarget.textContent = i18n.t("js.front.domain_search.number_of_results", {
      count: count,
      results_count: i18n.numberToDelimited(count),
    })
    this.resultSummaryTarget.classList.remove("d-none")
  }

  showFilters({ detail: { count } }) {
    if (!this.hasFiltersButtonTarget) {
      return
    }

    if (count > 0 && this.filtersButtonTarget.getAttribute("aria-expanded") !== "true") {
      this.filtersButtonTarget.click()
    }
  }

  toggleClearFilters() {
    if (!this.hasClearFiltersButtonTarget) {
      return
    }

    if (this.filtersValue.type || this.filtersValue.department || this.filtersValue.required_field) {
      this.clearFiltersButtonTarget.classList.remove("d-none")
    } else {
      this.clearFiltersButtonTarget.classList.add("d-none")
    }
  }

  // Update the title of results section
  updateResultsTitle({ detail: { count } }) {
    this.resultsTitleTarget.innerHTML = i18n.t("js.front.domain_search.results_title_html", {
      count: count,
      results_count: i18n.numberToDelimited(count),
    })
  }

  toggleFilters({ detail: { hidden } }) {
    this.filtersHidden = hidden
  }

  // Set filters passed from other stimulus controllers
  filter(event) {
    let filtersValue = this.filtersValue

    if (event.detail && event.detail.filters) {
      event.detail.filters.forEach((filter) => {
        filtersValue[filter.name] = filter.values.length === 0 ? null : filter.values
      })
    } else if (event.detail && event.detail.name) {
      filtersValue[event.detail.name] = event.detail.values.length === 0 ? null : event.detail.values
    }

    this.filtersValue = filtersValue
    this.loadResults()
  }

  // Clear all the filters
  clearFilters() {
    this.filtersValue = {}
    this.loadResults()
  }

  // Clean summary when user type a new domain
  cleanSummary() {
    if (this.domainInputTarget.value.length <= 1) {
      this.logoTarget.src = this.logoTarget.dataset.defaultLogo
      this.resultDomainTarget.classList.add("d-none")
      this.resultsCountTarget.classList.add("d-none")
    }
  }

  toggleSection(event) {
    const sectionId = event.currentTarget.dataset.sectionId
    const sectionName = event.currentTarget.dataset.sectionName
    const section = document.querySelector(sectionId)
    const sectionHasAnyResults = section.querySelectorAll(".ds-result").length > 0
    const sectionCheckbox = event.currentTarget.previousElementSibling
    sectionCheckbox.classList.toggle("d-none")

    if (sectionHasAnyResults) {
      return
    }

    // If section has no results, find Load more button and click it
    const loadMoreForm = this.loadMoreButtonTargets.find((x) => x.id.includes(sectionName)).querySelector("form")
    if (loadMoreForm) {
      loadMoreForm.requestSubmit()
    }
  }

  // Update the URL and browser history as needed
  updateBrowserUrl() {
    const referrerPath = window.location.pathname
    let parameters = ""

    if (this.filtersValue.type) {
      parameters = App.addUrlParam(parameters, "type", this.filtersValue.type)
    }

    if (this.filtersValue.already_saved_leads) {
      parameters = App.addUrlParam(parameters, "saved_leads", this.#savedLeadsFilterValue())
    }

    if (this.filtersValue.department) {
      parameters = App.addUrlParam(parameters, "department", this.filtersValue.department)
    }

    if (this.filtersValue.required_field) {
      parameters = App.addUrlParam(parameters, "required_field", this.filtersValue.required_field)
    }

    if (this.filtersValue.verification_status) {
      parameters = App.addUrlParam(parameters, "verification_status", this.filtersValue.verification_status)
    }

    if (this.filtersValue.job_titles) {
      parameters = App.addUrlParam(parameters, "job_titles", this.filtersValue.job_titles)
    }

    const newUrl = `/search/${this.domainValue}` + parameters
    history.replaceState({}, `Hunter: ${this.domainValue}`, newUrl)

    // Send a page view to Google Analytics if it's a search (and not a direct link to the page)
    if (referrerPath !== newUrl) {
      App.analytics.sendPageview(`Domain Search: ${this.domain}`, window.location.href, newUrl)
      App.analytics.posthog_capture("$pageview", { $current_url: newUrl })
    }
  }

  #typeFilterValue() {
    if (!this.hasFiltersValue || !this.filtersValue.type) {
      return null
    }

    if (this.filtersValue.type.includes("all")) {
      return null
    }

    return this.filtersValue.type
  }

  #parseCityValue(cityValue) {
    const parts = cityValue.split(",")
    return {
      city: parts[0],
      state: parts[1] || "",
      country: parts[2] || "",
    }
  }

  #alreadySavedLeadsFilterValue() {
    if (!this.hasFiltersValue || !this.filtersValue.already_saved_leads) {
      return null
    }

    if (!Array.isArray(this.filtersValue.already_saved_leads)) {
      return this.filtersValue.already_saved_leads
    }

    return this.filtersValue.already_saved_leads.find((v) => v)
  }

  #savedLeadsFilterValue() {
    const alreadySavedLeadsFilterValue = this.#alreadySavedLeadsFilterValue()

    if (alreadySavedLeadsFilterValue === "show") {
      return "true"
    } else if (alreadySavedLeadsFilterValue === "hide") {
      return "false"
    }
  }
}
